import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Masonry from 'react-masonry-css';
import {
    IconPlus, IconSearch, IconEdit, IconTrash, IconCopy, IconSparkles,
    IconAdjustmentsHorizontal, IconRefresh, IconTrendingUp, IconClock,
    IconSortAscendingLetters, IconArrowsSort, IconLoader2, IconX,
    IconLayoutGrid, IconRectangleVertical, IconRectangle, IconSquare,
    IconChevronDown, IconCalendar,
    IconUsers, IconGenderMale, IconGenderFemale, IconMoodKid, IconShoppingBag,
    IconPencil, IconWorldOff, IconUser, IconList, IconShare2,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useDebouncedValue } from '@/lib/hooks';
import aiTemplateService from '../../../services/aiTemplateService';
import aiTemplateCategoryService from '../../../services/aiTemplateCategoryService';
import { useAiTemplateCacheStore } from '../../../store/aiTemplateCacheStore';
import { imageColorBucket } from '../../../utils/imageColorBucket';
import { Sheet, SheetContent, SheetTitle } from '../../../components/ui/sheet';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuLabel, DropdownMenuSeparator,
    DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from '../../../components/ui/dropdown-menu';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import AiTemplateShareModal from './AiTemplateShareModal';

const PAGE_SIZE = 18;
const VIEW_KEY = 'adminAiTemplatesView';

// imageSize → orientation bucket used by the sidebar Orientation filter.
const ORIENTATION_OF = {
    portrait_2x3: 'portrait',
    portrait_4x5: 'portrait',
    square_1x1: 'square',
    landscape_3x2: 'landscape',
    landscape_16x9: 'landscape',
};
// Rough card height (height/width) per imageSize, used to balance the masonry
// before the thumbnail loads. Refined with the measured ratio once loaded.
const IMG_ASPECT_H = {
    portrait_2x3: 1.5,
    portrait_4x5: 1.25,
    square_1x1: 1.0,
    landscape_3x2: 0.667,
    landscape_16x9: 0.5625,
};
const IMG_ASPECT_RATIO = {
    portrait_2x3: '2 / 3',
    portrait_4x5: '4 / 5',
    square_1x1: '1 / 1',
    landscape_3x2: '3 / 2',
    landscape_16x9: '16 / 9',
};
const CARD_FOOTER_H = 0.32;
const masonryBreakpoints = { default: 5, 1920: 5, 1440: 4, 1280: 3, 1024: 2, 640: 1 };

const STATUSES = ['active', 'draft', 'archived'];
const STATUS_PILL = {
    active:   'bg-emerald-500/90 text-white',
    draft:    'bg-amber-500/90 text-white',
    archived: 'bg-zinc-600/85 text-white',
};

// 'today' | 'yesterday' | null — local-calendar recency of a createdAt date,
// shown as a badge on draft cards so freshly bulk-created drafts stand out.
function recencyOf(createdAt) {
    if (!createdAt) return null;
    const d = new Date(createdAt);
    if (Number.isNaN(d.getTime())) return null;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    if (d >= startOfToday) return 'today';
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    if (d >= startOfYesterday) return 'yesterday';
    return null;
}

// Date-range filter → epoch-ms cutoff. Templates with createdAt >= cutoff pass.
function dateRangeCutoff(range) {
    const now = new Date();
    if (range === 'today') {
        const d = new Date(now);
        d.setHours(0, 0, 0, 0);
        return d.getTime();
    }
    const days = { '3days': 3, '7days': 7, '30days': 30 }[range];
    if (!days) return null;
    return now.getTime() - days * 24 * 60 * 60 * 1000;
}

// Greedy balanced masonry — each card goes to the currently-shortest column so
// columns stay even (unlike react-masonry-css's index-based distribution).
function BalancedMasonry({ items, columnCount, renderItem, heightOf }) {
    const columns = useMemo(() => {
        const cols = Array.from({ length: columnCount }, () => []);
        const heights = new Array(columnCount).fill(0);
        for (const it of items) {
            const est = heightOf(it);
            let min = 0;
            for (let i = 1; i < columnCount; i++) if (heights[i] < heights[min]) min = i;
            cols[min].push(it);
            heights[min] += est;
        }
        return cols;
    }, [items, columnCount, heightOf]);

    return (
        <div className="flex w-auto -ml-4">
            {columns.map((col, i) => (
                <div key={i} className="pl-4 bg-clip-padding flex-1 min-w-0">
                    {col.map(renderItem)}
                </div>
            ))}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Admin AI-templates gallery — 1:1 clone of the user gallery + admin actions
// ---------------------------------------------------------------------------
export default function AdminAiTemplatesPage() {
    const { t, i18n } = useTranslation();
    const isBn = i18n.language === 'bn';
    const fmt = (n) => (n ?? 0).toLocaleString(isBn ? 'bn-BD' : 'en-US');
    const getCatName = (cat) => (isBn && cat?.nameBn) ? cat.nameBn : cat?.name;
    const getCatDesc = (cat) => (isBn && cat?.descriptionBn) ? cat.descriptionBn : cat?.description;

    const [templates, setTemplates] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [allTags, setAllTags] = useState([]);

    // ── Column count from the actual grid width (~250px per card) ──
    const gridRef = useRef(null);
    const [gridWidth, setGridWidth] = useState(0);
    useEffect(() => {
        const el = gridRef.current;
        if (!el) return;
        const measure = () => setGridWidth(el.clientWidth);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const columnCount = gridWidth ? Math.max(1, Math.min(6, Math.floor(gridWidth / 250))) : 3;

    const [ratios, setRatios] = useState({});
    const reportRatio = useCallback((id, r) => {
        if (!r || !isFinite(r)) return;
        setRatios((prev) => (prev[id] ? prev : { ...prev, [id]: r }));
    }, []);
    const heightOf = useCallback(
        (it) => (ratios[it.id] ?? IMG_ASPECT_H[it.imageSize] ?? 1.333) + CARD_FOOTER_H,
        [ratios]
    );

    // ── Filter state ──
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch] = useDebouncedValue(searchQuery, 300);
    const [activeCategory, setActiveCategory] = useState('all');
    // ?status=draft deep-links to a pre-filtered list (used by bulk draft creation).
    const [searchParams] = useSearchParams();
    const [statusFilter, setStatusFilter] = useState(() =>
        STATUSES.includes(searchParams.get('status')) ? searchParams.get('status') : 'all');
    const [orientation, setOrientation] = useState('all');
    const [imageCount, setImageCount] = useState('all');
    const [color, setColor] = useState('all');
    const [gender, setGender] = useState('all');
    const [sort, setSort] = useState('order');
    const [sortDir, setSortDir] = useState('desc');
    const [dateRange, setDateRange] = useState('all');
    const [activeTag, setActiveTag] = useState(null);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);
    const [colorBuckets, setColorBuckets] = useState({});
    const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || 'grid');
    const changeView = (v) => { setView(v); localStorage.setItem(VIEW_KEY, v); };

    // ── Action state ──
    const [deleteModal, setDeleteModal] = useState({ opened: false, template: null });
    // Template being shared via the promo dialog (null = closed).
    const [shareTpl, setShareTpl] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [duplicatingId, setDuplicatingId] = useState(null);
    const [togglingId, setTogglingId] = useState(null);

    // ── Fetch (admin sees ALL statuses; server search, filters client-side) ──
    const fetchTemplates = useCallback(async () => {
        setLoading(true);
        try {
            const params = { status: 'all' };
            if (debouncedSearch) params.search = debouncedSearch.trim();
            const response = await aiTemplateService.list(params);
            setTemplates(response?.data || []);
        } catch {
            notifications.show({ title: t('aiTemplatesAdmin.error'), message: t('aiTemplatesAdmin.failedToLoad'), color: 'red' });
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { fetchTemplates(); }, [fetchTemplates]);
    useEffect(() => {
        aiTemplateCategoryService.list().then(r => setCategories(r?.data || [])).catch(() => {});
        aiTemplateService.tags().then(r => setAllTags((r?.data || []).map(tg => tg.name).filter(Boolean))).catch(() => {});
    }, []);

    // Detect colour buckets from thumbnails (once per template) for the colour filter.
    useEffect(() => {
        let cancelled = false;
        templates.forEach((tp) => {
            if (!tp.thumbnail || colorBuckets[tp.id] !== undefined) return;
            imageColorBucket(tp.thumbnail).then((bucket) => {
                if (!cancelled) setColorBuckets(prev => (prev[tp.id] !== undefined ? prev : { ...prev, [tp.id]: bucket }));
            });
        });
        return () => { cancelled = true; };
    }, [templates]); // eslint-disable-line react-hooks/exhaustive-deps

    // Tag marquee — constant px/sec speed regardless of tag count.
    const TAG_MARQUEE_SPEED = 22;
    const tagRowRef = useRef(null);
    const [tagMarqueeDuration, setTagMarqueeDuration] = useState(60);
    const [tagMarqueePaused, setTagMarqueePaused] = useState(false);
    useEffect(() => {
        const el = tagRowRef.current;
        if (!el || allTags.length === 0) return;
        const measure = () => {
            const oneSetWidth = el.scrollWidth / 2;
            if (oneSetWidth > 0) setTagMarqueeDuration(oneSetWidth / TAG_MARQUEE_SPEED);
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [allTags]);

    // ── Actions ──
    const handleStatusToggle = async (tmpl) => {
        const nextStatus = tmpl.status === 'active' ? 'draft' : 'active';
        setTogglingId(tmpl.id);
        try {
            const fd = new FormData();
            fd.append('status', nextStatus);
            await aiTemplateService.update(tmpl.id, fd);
            useAiTemplateCacheStore.getState().clear();
            setTemplates(prev => prev.map(x => x.id === tmpl.id ? { ...x, status: nextStatus } : x));
        } catch {
            notifications.show({ title: t('aiTemplatesAdmin.error'), message: t('aiTemplatesAdmin.failedToUpdateStatus'), color: 'red' });
        } finally {
            setTogglingId(null);
        }
    };

    const handleDuplicate = async (tpl) => {
        setDuplicatingId(tpl.id);
        try {
            const r = await aiTemplateService.duplicate(tpl.id);
            useAiTemplateCacheStore.getState().clear();
            notifications.show({ title: t('aiTemplatesAdmin.duplicated'), message: t('aiTemplatesAdmin.createdAsDraft', { name: r?.data?.name || t('aiTemplatesAdmin.copy') }), color: 'green' });
            fetchTemplates();
        } catch {
            notifications.show({ title: t('aiTemplatesAdmin.error'), message: t('aiTemplatesAdmin.failedToDuplicate'), color: 'red' });
        } finally {
            setDuplicatingId(null);
        }
    };

    const handleDeleteConfirm = async () => {
        setDeleting(true);
        try {
            await aiTemplateService.remove(deleteModal.template.id);
            useAiTemplateCacheStore.getState().clear();
            notifications.show({ title: t('aiTemplatesAdmin.success'), message: t('aiTemplatesAdmin.templateDeleted'), color: 'green' });
            setDeleteModal({ opened: false, template: null });
            fetchTemplates();
        } catch {
            notifications.show({ title: t('aiTemplatesAdmin.error'), message: t('aiTemplatesAdmin.failedToDelete'), color: 'red' });
        } finally {
            setDeleting(false);
        }
    };

    // ── Derived ──
    const activeCategoryObj = activeCategory === 'all' ? null : categories.find(c => c.slug === activeCategory);
    const activeCategoryId = activeCategoryObj?.id ?? null;

    const countByCatId = useMemo(() => {
        const map = new Map();
        for (const tp of templates) {
            if (tp.categoryId == null) continue;
            map.set(tp.categoryId, (map.get(tp.categoryId) || 0) + 1);
        }
        return map;
    }, [templates]);

    const countByStatus = useMemo(() => {
        const map = { active: 0, draft: 0, archived: 0 };
        for (const tp of templates) if (map[tp.status] != null) map[tp.status] += 1;
        return map;
    }, [templates]);

    const imgCountOf = (tp) => {
        const n = tp.requiredImageCount || 1;
        if (n >= 3) return '3+';
        if (n === 2) return '2';
        return '1';
    };

    const filtered = useMemo(() => {
        let list = templates;
        if (activeCategoryId != null) list = list.filter(tp => tp.categoryId === activeCategoryId);
        if (statusFilter !== 'all') list = list.filter(tp => tp.status === statusFilter);
        if (orientation !== 'all') list = list.filter(tp => (ORIENTATION_OF[tp.imageSize] || 'portrait') === orientation);
        if (imageCount !== 'all') list = list.filter(tp => imgCountOf(tp) === imageCount);
        if (color !== 'all') list = list.filter(tp => colorBuckets[tp.id] === color);
        if (gender !== 'all') list = list.filter(tp => Array.isArray(tp.genders) && tp.genders.includes(gender));
        if (dateRange !== 'all') {
            const cutoff = dateRangeCutoff(dateRange);
            if (cutoff != null) list = list.filter(tp => new Date(tp.createdAt || 0).getTime() >= cutoff);
        }
        if (activeTag) list = list.filter(tp => (tp.tags || []).some(tg => tg.toLowerCase() === activeTag.toLowerCase()));

        const sorted = [...list];
        const dir = sortDir === 'asc' ? -1 : 1;
        if (sort === 'order') sorted.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        else if (sort === 'popular') sorted.sort((a, b) => dir * ((b.usageCount || 0) - (a.usageCount || 0)) || (b.id > a.id ? 1 : -1));
        else if (sort === 'newest') sorted.sort((a, b) => dir * (new Date(b.createdAt || 0) - new Date(a.createdAt || 0)) || (b.id > a.id ? 1 : -1));
        else if (sort === 'oldest') sorted.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0) || (a.id > b.id ? 1 : -1));
        else if (sort === 'name') sorted.sort((a, b) => String(a.nameBn || a.name || '').localeCompare(String(b.nameBn || b.name || ''), i18n.language));
        return sorted;
    }, [templates, activeCategoryId, statusFilter, orientation, imageCount, color, colorBuckets, gender, sort, sortDir, dateRange, activeTag, i18n.language]); // eslint-disable-line

    useEffect(() => { setVisibleCount(PAGE_SIZE); }, [activeCategory, statusFilter, orientation, imageCount, color, gender, sort, sortDir, dateRange, activeTag, debouncedSearch]);

    const shown = filtered.slice(0, visibleCount);
    const hasMore = filtered.length > visibleCount;

    const sentinelRef = useRef(null);
    useEffect(() => {
        if (!hasMore) return;
        const node = sentinelRef.current;
        if (!node) return;
        const observer = new IntersectionObserver(
            (entries) => { if (entries[0]?.isIntersecting) setVisibleCount(c => c + PAGE_SIZE); },
            { rootMargin: '400px' }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, shown.length]);

    const filtersDirty = activeCategory !== 'all' || statusFilter !== 'all' || orientation !== 'all' || imageCount !== 'all' || color !== 'all' || gender !== 'all' || sort !== 'order' || sortDir !== 'desc' || dateRange !== 'all' || !!activeTag || !!searchQuery;

    const resetFilters = () => {
        setActiveCategory('all');
        setStatusFilter('all');
        setOrientation('all');
        setImageCount('all');
        setColor('all');
        setGender('all');
        setSort('order');
        setSortDir('desc');
        setDateRange('all');
        setActiveTag(null);
        setSearchQuery('');
    };

    // Filter controls shared between the desktop sidebar and mobile drawer.
    const filterHeaderRow = (
        <div className="flex items-center gap-2 mb-4">
            <IconAdjustmentsHorizontal size={17} className="text-[#E2702E]" />
            <span className="text-sm font-bold text-[#1C1B19] dark:text-zinc-100">{t('worldCupJersey.filters')}</span>
            <button
                onClick={resetFilters}
                disabled={!filtersDirty}
                className="ml-auto inline-flex items-center gap-1 text-xs font-semibold transition-colors text-[#E2702E] hover:text-[#E2702E]/80 disabled:text-[#C3BEB4] dark:disabled:text-zinc-600 disabled:cursor-not-allowed"
            >
                <IconRefresh size={13} /> {t('worldCupJersey.reset')}
            </button>
        </div>
    );

    const filterBody = (
        <>
            {/* Category */}
            <FilterLabel>{t('worldCupJersey.category')}</FilterLabel>
            <div className={`flex flex-col gap-0.5 mb-5 ${categories.length + 1 > 10 ? 'max-h-[360px] overflow-y-auto pr-1 [scrollbar-width:thin]' : ''}`}>
                <CategoryRow active={activeCategory === 'all'} label={t('worldCupJersey.all')} count={fmt(templates.length)} onClick={() => setActiveCategory('all')} />
                {categories.map(c => (
                    <CategoryRow key={c.slug} active={activeCategory === c.slug} label={getCatName(c)} count={fmt(countByCatId.get(c.id) || 0)} onClick={() => setActiveCategory(c.slug)} />
                ))}
            </div>

            {/* Status (admin-only) */}
            <FilterLabel>{t('aiTemplatesAdmin.status')}</FilterLabel>
            <div className="flex flex-col gap-0.5 mb-5">
                <CategoryRow active={statusFilter === 'all'} label={t('worldCupJersey.all')} count={fmt(templates.length)} onClick={() => setStatusFilter('all')} />
                {STATUSES.map(s => (
                    <CategoryRow key={s} active={statusFilter === s} label={t(`aiTemplatesAdmin.status_${s}`)} count={fmt(countByStatus[s] || 0)} onClick={() => setStatusFilter(s)} />
                ))}
            </div>

            {/* Gender / audience */}
            <FilterLabel>{t('worldCupJersey.genderLabel')}</FilterLabel>
            <div className="grid grid-cols-3 gap-1.5 mb-5">
                <OrientationBtn active={gender === 'all'} icon={IconUsers} label={t('worldCupJersey.all')} onClick={() => setGender('all')} />
                <OrientationBtn active={gender === 'male'} icon={IconGenderMale} label={t('worldCupJersey.genderMale')} onClick={() => setGender('male')} />
                <OrientationBtn active={gender === 'female'} icon={IconGenderFemale} label={t('worldCupJersey.genderFemale')} onClick={() => setGender('female')} />
                <OrientationBtn active={gender === 'children'} icon={IconMoodKid} label={t('worldCupJersey.genderChildren')} onClick={() => setGender('children')} />
                <OrientationBtn active={gender === 'product'} icon={IconShoppingBag} label={t('worldCupJersey.genderProduct')} onClick={() => setGender('product')} />
            </div>

            {/* Image count */}
            <FilterLabel>{t('worldCupJersey.imageCountLabel')}</FilterLabel>
            <div className="flex flex-col gap-0.5 mb-5">
                <RadioRow active={imageCount === 'all'} title={t('worldCupJersey.all')} onClick={() => setImageCount('all')} />
                <RadioRow active={imageCount === '1'} title={t('worldCupJersey.singleImage')} hint={t('worldCupJersey.singleImageHint')} onClick={() => setImageCount('1')} />
                <RadioRow active={imageCount === '2'} title={t('worldCupJersey.dualImage')} hint={t('worldCupJersey.dualImageHint')} onClick={() => setImageCount('2')} />
                <RadioRow active={imageCount === '3+'} title={t('worldCupJersey.multiImage')} hint={t('worldCupJersey.multiImageHint')} onClick={() => setImageCount('3+')} />
            </div>

            {/* Orientation */}
            <FilterLabel>{t('worldCupJersey.orientation')}</FilterLabel>
            <div className="grid grid-cols-4 gap-1.5 mb-5">
                <OrientationBtn active={orientation === 'all'} icon={IconLayoutGrid} label={t('worldCupJersey.all')} onClick={() => setOrientation('all')} />
                <OrientationBtn active={orientation === 'portrait'} icon={IconRectangleVertical} label={t('worldCupJersey.portrait')} onClick={() => setOrientation('portrait')} />
                <OrientationBtn active={orientation === 'landscape'} icon={IconRectangle} label={t('worldCupJersey.landscape')} onClick={() => setOrientation('landscape')} />
                <OrientationBtn active={orientation === 'square'} icon={IconSquare} label={t('worldCupJersey.square')} onClick={() => setOrientation('square')} />
            </div>

            {/* Colour */}
            <FilterLabel>{t('worldCupJersey.color')}</FilterLabel>
            <div className="flex flex-wrap gap-2.5 mb-5">
                <Swatch active={color === 'all'} title={t('worldCupJersey.colorAll')} swatchCls="bg-[conic-gradient(from_0deg,#ef4444,#f59e0b,#eab308,#22c55e,#3b82f6,#a855f7,#ef4444)]" onClick={() => setColor('all')} />
                <Swatch active={color === 'red'} title={t('worldCupJersey.colorRed')} swatchCls="bg-red-500" onClick={() => setColor('red')} />
                <Swatch active={color === 'orange'} title={t('worldCupJersey.colorOrange')} swatchCls="bg-orange-500" onClick={() => setColor('orange')} />
                <Swatch active={color === 'yellow'} title={t('worldCupJersey.colorYellow')} swatchCls="bg-yellow-400" onClick={() => setColor('yellow')} />
                <Swatch active={color === 'green'} title={t('worldCupJersey.colorGreen')} swatchCls="bg-green-500" onClick={() => setColor('green')} />
                <Swatch active={color === 'blue'} title={t('worldCupJersey.colorBlue')} swatchCls="bg-blue-500" onClick={() => setColor('blue')} />
                <Swatch active={color === 'purple'} title={t('worldCupJersey.colorPurple')} swatchCls="bg-purple-500" onClick={() => setColor('purple')} />
                <Swatch active={color === 'neutral'} title={t('worldCupJersey.colorNeutral')} swatchCls="bg-[linear-gradient(135deg,#a1a1aa,#3f3f46)]" onClick={() => setColor('neutral')} />
            </div>

            {/* Sort */}
            <FilterLabel>{t('worldCupJersey.sort')}</FilterLabel>
            <div className="flex flex-col gap-0.5">
                <SortRow active={sort === 'order'} icon={IconArrowsSort} label={t('aiTemplatesAdmin.sortOrder')} onClick={() => setSort('order')} />
                <SortRow active={sort === 'popular'} icon={IconTrendingUp} label={t('worldCupJersey.sortPopular')} onClick={() => setSort('popular')} />
                <SortRow active={sort === 'newest'} icon={IconClock} label={t('worldCupJersey.sortNewest')} onClick={() => setSort('newest')} />
                <SortRow active={sort === 'oldest'} icon={IconClock} label={t('worldCupJersey.sortOldest')} onClick={() => setSort('oldest')} />
                <SortRow active={sort === 'name'} icon={IconSortAscendingLetters} label={t('worldCupJersey.sortName')} onClick={() => setSort('name')} />
            </div>
        </>
    );

    return (
        <div className="font-hind w-full py-4 md:py-6">
            {/* Header */}
            <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <h1 className="text-2xl font-bold tracking-[-0.01em] text-[#1C1B19] dark:text-zinc-100">
                        {activeCategoryObj ? getCatName(activeCategoryObj) : t('aiTemplatesAdmin.pageTitle')}
                    </h1>
                    <p className="mt-1 text-sm text-[#6E6A63] dark:text-zinc-400">
                        {activeCategoryObj ? getCatDesc(activeCategoryObj) || t('aiTemplatesAdmin.pageSubtitle') : t('aiTemplatesAdmin.pageSubtitle')}
                    </p>
                </div>
                <div className="flex-shrink-0 flex items-center gap-2">
                    {/* Quick sort: Newest / Popular */}
                    <div className="hidden sm:flex items-center gap-0.5 p-0.5 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900">
                        <QuickSortPill active={sort === 'newest'} icon={IconClock} label={t('worldCupJersey.sortNewest')} onClick={() => setSort('newest')} />
                        <QuickSortPill active={sort === 'popular'} icon={IconTrendingUp} label={t('worldCupJersey.sortPopular')} onClick={() => setSort('popular')} />
                    </div>
                    {/* Quick Draft filter — toggles status between draft-only and all */}
                    <button
                        onClick={() => setStatusFilter(s => (s === 'draft' ? 'all' : 'draft'))}
                        title={t('aiTemplatesAdmin.status_draft')}
                        className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-[11px] border text-[13px] font-semibold transition-colors ${
                            statusFilter === 'draft'
                                ? 'border-amber-500 bg-amber-500 text-white'
                                : 'border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30'
                        }`}
                    >
                        <IconPencil size={15} className={statusFilter === 'draft' ? 'text-white' : 'text-amber-500'} />
                        <span className="hidden sm:inline">{t('aiTemplatesAdmin.status_draft')}</span>
                    </button>
                    <Link
                        to="/admin/ai-templates/new"
                        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-[10px] bg-[#E2702E] hover:bg-[#E2702E]/90 text-white text-[13px] font-semibold transition-colors"
                    >
                        <IconPlus size={16} stroke={2} /> {t('aiTemplatesAdmin.addNewTemplate')}
                    </Link>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-5 items-start">

                {/* ── Sidebar (desktop) ── */}
                <aside className="hidden lg:block lg:w-72 lg:flex-shrink-0 lg:sticky lg:top-4 self-start bg-white dark:bg-zinc-900 border border-[#ECE9E3] dark:border-zinc-700 rounded-2xl p-4">
                    {filterHeaderRow}
                    {filterBody}
                </aside>

                {/* ── Main ── */}
                <main ref={gridRef} className="flex-1 min-w-0 w-full">
                    {/* Search + mobile filter trigger */}
                    <div className="flex items-center gap-2 mb-3">
                        <div className="flex items-center gap-2.5 h-12 px-3.5 flex-1 min-w-0 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900">
                            <IconSearch size={18} className="text-[#9C988F] dark:text-zinc-400 flex-shrink-0" />
                            <input
                                type="text"
                                className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-[#1C1B19] dark:text-zinc-100 placeholder:text-[#9C988F] dark:placeholder:text-zinc-400"
                                placeholder={t('aiTemplatesAdmin.searchPlaceholder')}
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button type="button" onClick={() => setSearchQuery('')} className="flex-shrink-0 p-0.5 rounded-full text-[#9C988F] dark:text-zinc-400 hover:text-[#E2702E] hover:bg-[#F1EFEA] dark:hover:bg-zinc-800 transition-colors">
                                    <IconX size={16} />
                                </button>
                            )}
                        </div>
                        <button
                            onClick={() => setFilterSheetOpen(true)}
                            className="lg:hidden relative flex-shrink-0 inline-flex items-center gap-1.5 h-12 px-4 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm font-semibold text-[#1C1B19] dark:text-zinc-100 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 transition-colors"
                        >
                            <IconAdjustmentsHorizontal size={18} className="text-[#E2702E]" />
                            <span className="hidden sm:inline">{t('worldCupJersey.filters')}</span>
                            {filtersDirty && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#E2702E]" />}
                        </button>
                    </div>

                    {/* Tag marquee + result count + sort */}
                    <div className="flex items-center gap-3 mb-4">
                        {allTags.length > 0 ? (
                            <div
                                className="flex-1 min-w-0 relative overflow-hidden"
                                onMouseEnter={() => setTagMarqueePaused(true)}
                                onMouseLeave={() => setTagMarqueePaused(false)}
                                style={{ WebkitMaskImage: 'linear-gradient(to right, transparent, #000 3%, #000 92%, transparent)', maskImage: 'linear-gradient(to right, transparent, #000 3%, #000 92%, transparent)' }}
                            >
                                <div
                                    ref={tagRowRef}
                                    className="flex gap-2 w-max py-0.5"
                                    style={{
                                        animationName: 'admin-atg-tag-marquee',
                                        animationDuration: `${tagMarqueeDuration}s`,
                                        animationTimingFunction: 'linear',
                                        animationIterationCount: 'infinite',
                                        animationPlayState: tagMarqueePaused ? 'paused' : 'running',
                                    }}
                                >
                                    {[...allTags, ...allTags].map((tag, i) => {
                                        const active = activeTag && activeTag.toLowerCase() === tag.toLowerCase();
                                        return (
                                            <button
                                                key={`${tag}-${i}`}
                                                onClick={() => setActiveTag(active ? null : tag)}
                                                className={`flex-shrink-0 text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${active
                                                    ? 'bg-[#E2702E] border-[#E2702E] text-white'
                                                    : 'text-[#6E6A63] dark:text-zinc-300 bg-white dark:bg-zinc-800 border-[#E9E6E0] dark:border-zinc-700 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 hover:text-[#E2702E]'}`}
                                            >
                                                #{tag}
                                            </button>
                                        );
                                    })}
                                </div>
                                <style>{`@keyframes admin-atg-tag-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
                            </div>
                        ) : <div className="flex-1" />}
                        <div className="flex-shrink-0 flex items-center gap-2.5">
                            <p className="text-[13px] text-[#6E6A63] dark:text-zinc-300 whitespace-nowrap hidden sm:block">
                                {fmt(filtered.length)} {t('worldCupJersey.templatesFound')}
                            </p>
                            {/* Grid / list view toggle */}
                            <div className="flex items-center gap-0.5 p-0.5 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900">
                                {[
                                    { key: 'grid', Icon: IconLayoutGrid, label: t('questionsCreate.viewGrid') },
                                    { key: 'list', Icon: IconList, label: t('questionsCreate.viewList') },
                                ].map(({ key, Icon, label }) => (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => changeView(key)}
                                        title={label}
                                        aria-label={label}
                                        aria-pressed={view === key}
                                        className={`flex h-8 w-8 items-center justify-center rounded-[9px] transition-colors cursor-pointer ${
                                            view === key
                                                ? 'bg-[#E2702E] text-white'
                                                : 'text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 hover:text-[#E2702E]'
                                        }`}
                                    >
                                        <Icon size={16} />
                                    </button>
                                ))}
                            </div>
                            <SortMenu sort={sort} setSort={setSort} dateRange={dateRange} setDateRange={setDateRange} />
                        </div>
                    </div>

                    {/* Active tag banner */}
                    {activeTag && (
                        <div className="flex items-center gap-2 mb-4">
                            <span className="text-sm text-zinc-500 dark:text-zinc-400">{t('worldCupJersey.filters')}:</span>
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[#E2702E]/10 text-[#E2702E]">
                                #{activeTag}
                                <button onClick={() => setActiveTag(null)} className="hover:text-red-500 transition-colors"><IconX size={12} /></button>
                            </span>
                        </div>
                    )}

                    {/* Skeleton */}
                    {loading && (view === 'grid' ? <SkeletonMasonry /> : <SkeletonList />)}

                    {/* Empty */}
                    {!loading && filtered.length === 0 && (
                        <div className="flex flex-col items-center gap-3 py-20">
                            <div className="w-16 h-16 rounded-2xl bg-[#F1EFEA] dark:bg-zinc-800 flex items-center justify-center">
                                <IconSparkles size={28} className="text-[#C3BEB4] dark:text-zinc-600" />
                            </div>
                            <p className="text-sm text-[#6E6A63] dark:text-zinc-400">{filtersDirty ? t('aiTemplatesAdmin.noMatchingTemplates') : t('aiTemplatesAdmin.noTemplatesYet')}</p>
                            {!filtersDirty && (
                                <Link to="/admin/ai-templates/new" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[10px] bg-[#E2702E] hover:bg-[#E2702E]/90 text-white text-sm font-semibold transition-colors">
                                    <IconPlus size={16} /> {t('aiTemplatesAdmin.createFirstTemplate')}
                                </Link>
                            )}
                        </div>
                    )}

                    {/* Grid / list */}
                    {!loading && filtered.length > 0 && (
                        <>
                            {view === 'grid' ? (
                                <BalancedMasonry
                                    items={shown}
                                    columnCount={columnCount}
                                    heightOf={heightOf}
                                    renderItem={(tpl) => (
                                        <AdminTemplateCard
                                            key={tpl.id}
                                            template={tpl}
                                            isBn={isBn}
                                            togglingId={togglingId}
                                            duplicatingId={duplicatingId}
                                            onMeasure={reportRatio}
                                            onToggleStatus={handleStatusToggle}
                                            onDuplicate={handleDuplicate}
                                            onShare={setShareTpl}
                                            onDelete={() => setDeleteModal({ opened: true, template: tpl })}
                                            onTagClick={setActiveTag}
                                        />
                                    )}
                                />
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {shown.map((tpl) => (
                                        <AdminTemplateListRow
                                            key={tpl.id}
                                            template={tpl}
                                            isBn={isBn}
                                            togglingId={togglingId}
                                            duplicatingId={duplicatingId}
                                            onToggleStatus={handleStatusToggle}
                                            onDuplicate={handleDuplicate}
                                            onShare={setShareTpl}
                                            onDelete={() => setDeleteModal({ opened: true, template: tpl })}
                                            onTagClick={setActiveTag}
                                        />
                                    ))}
                                </div>
                            )}

                            {hasMore && (
                                <div ref={sentinelRef} className="flex justify-center items-center gap-2 mt-6 py-4 text-[#E2702E]">
                                    <IconLoader2 size={20} className="animate-spin" />
                                    <span className="text-sm font-medium">{t('common.loading')}</span>
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>

            {/* Filter drawer (mobile) */}
            <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
                <SheetContent side="left" className="w-[86%] max-w-xs overflow-y-auto p-4 font-hind">
                    <SheetTitle className="sr-only">{t('worldCupJersey.filters')}</SheetTitle>
                    <div className="pr-7">{filterHeaderRow}</div>
                    {filterBody}
                </SheetContent>
            </Sheet>

            {/* Share (promo) dialog — caption + user-side link + image copy/download */}
            <AiTemplateShareModal
                template={shareTpl}
                open={!!shareTpl}
                onOpenChange={(o) => { if (!o) setShareTpl(null); }}
            />

            {/* Delete confirm */}
            <ConfirmDialog
                open={deleteModal.opened}
                onClose={() => setDeleteModal({ opened: false, template: null })}
                onConfirm={handleDeleteConfirm}
                title={t('aiTemplatesAdmin.confirmDelete')}
                description={t('aiTemplatesAdmin.deleteConfirmDesc', { name: deleteModal.template?.name })}
                loading={deleting}
            />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Sidebar primitives
// ---------------------------------------------------------------------------
function FilterLabel({ children }) {
    return <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9C988F] dark:text-zinc-400 mb-2">{children}</p>;
}

function CategoryRow({ active, label, count, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center justify-between px-3 py-2 rounded-lg text-[13px] transition-colors ${
                active ? 'font-semibold text-[#E2702E] bg-[#FCEFE7] dark:bg-orange-950/30' : 'font-medium text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800'
            }`}
        >
            <span className="truncate text-left">{label}</span>
            <span className={`ml-2 flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                active ? 'text-[#E2702E] bg-white dark:bg-zinc-900' : 'text-[#A8A294] dark:text-zinc-400 bg-[#F1EFEA] dark:bg-zinc-800'
            }`}>{count}</span>
        </button>
    );
}

function OrientationBtn({ active, icon: Icon, label, onClick }) {
    return (
        <button
            onClick={onClick}
            title={label}
            className={`flex flex-col items-center justify-center gap-1 py-2 rounded-lg text-[11px] font-semibold border transition-colors ${
                active ? 'border-[#E2702E] bg-[#FCEFE7] dark:bg-orange-950/30 text-[#E2702E]' : 'border-[#ECE9E3] dark:border-zinc-700 text-[#6E6A63] dark:text-zinc-300 hover:border-[#E2702E]/50'
            }`}
        >
            <Icon size={15} />
            <span className="truncate w-full text-center px-0.5">{label}</span>
        </button>
    );
}

function RadioRow({ active, title, hint, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors ${
                active ? 'bg-[#FCEFE7] dark:bg-orange-950/30' : 'hover:bg-[#FAF9F6] dark:hover:bg-zinc-800'
            }`}
        >
            <span className={`w-4 h-4 rounded-full flex-shrink-0 border-[1.5px] transition-colors ${
                active ? 'border-[4.5px] border-[#E2702E]' : 'border-[#C3BEB4] dark:border-zinc-600'
            }`} />
            <span className="flex flex-col leading-tight">
                <span className="text-[13px] font-medium text-[#1C1B19] dark:text-zinc-100">{title}</span>
                {hint && <span className="text-[10.5px] text-[#9C988F] dark:text-zinc-400">{hint}</span>}
            </span>
        </button>
    );
}

function Swatch({ active, title, swatchCls, onClick }) {
    return (
        <button
            onClick={onClick}
            title={title}
            className={`w-[34px] h-[34px] rounded-full transition-all ${swatchCls} ${
                active ? 'outline outline-2 outline-[#E2702E] outline-offset-2' : 'outline outline-1 outline-[#ECE9E3] outline-offset-2'
            }`}
        />
    );
}

function QuickSortPill({ active, icon: Icon, label, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-[9px] text-[13px] font-semibold transition-colors cursor-pointer ${
                active ? 'bg-[#E2702E] text-white' : 'text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30'
            }`}
        >
            <Icon size={15} className={active ? 'text-white' : 'text-[#E2702E]'} />
            <span className="hidden md:inline">{label}</span>
        </button>
    );
}

function SortRow({ active, icon: Icon, label, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] transition-colors ${
                active ? 'font-semibold text-[#E2702E] bg-[#FCEFE7] dark:bg-orange-950/30' : 'font-medium text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800'
            }`}
        >
            <Icon size={14} />
            {label}
        </button>
    );
}

// Top-right Sort / date-range dropdown.
function SortMenu({ sort, setSort, dateRange, setDateRange }) {
    const { t } = useTranslation();
    const sortOptions = [
        { value: 'order', label: t('aiTemplatesAdmin.sortOrder') },
        { value: 'popular', label: t('worldCupJersey.sortPopular') },
        { value: 'newest', label: t('worldCupJersey.sortNewest') },
        { value: 'oldest', label: t('worldCupJersey.sortOldest') },
        { value: 'name', label: t('worldCupJersey.sortName') },
    ];
    const dateOptions = [
        { value: 'all', label: t('worldCupJersey.dateAll') },
        { value: 'today', label: t('worldCupJersey.dateToday') },
        { value: '3days', label: t('worldCupJersey.date3Days') },
        { value: '7days', label: t('worldCupJersey.date7Days') },
        { value: '30days', label: t('worldCupJersey.date30Days') },
    ];
    const active = sort !== 'order' || dateRange !== 'all';

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button className="relative inline-flex items-center gap-1.5 h-9 px-3 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[13px] font-semibold text-[#1C1B19] dark:text-zinc-100 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 transition-colors cursor-pointer">
                    <IconArrowsSort size={16} className="text-[#E2702E]" />
                    <span className="hidden sm:inline">{t('worldCupJersey.sortAction')}</span>
                    <IconChevronDown size={14} className="text-[#9C988F] dark:text-zinc-400" />
                    {active && <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#E2702E]" />}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 font-hind">
                <DropdownMenuLabel className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#9C988F] dark:text-zinc-400">
                    <IconArrowsSort size={13} /> {t('worldCupJersey.sortBy')}
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup value={sort} onValueChange={setSort}>
                    {sortOptions.map(o => (
                        <DropdownMenuRadioItem key={o.value} value={o.value} className="text-[13px] cursor-pointer">{o.label}</DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#9C988F] dark:text-zinc-400">
                    <IconCalendar size={13} /> {t('worldCupJersey.dateRange')}
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup value={dateRange} onValueChange={setDateRange}>
                    {dateOptions.map(o => (
                        <DropdownMenuRadioItem key={o.value} value={o.value} className="text-[13px] cursor-pointer">{o.label}</DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

// ---------------------------------------------------------------------------
// Admin template card — user-gallery look (hover overlay, tags) + admin actions
// ---------------------------------------------------------------------------
function AdminTemplateCard({ template, isBn, togglingId, duplicatingId, onMeasure, onToggleStatus, onDuplicate, onShare, onDelete, onTagClick }) {
    const { t } = useTranslation();
    const displayName = (isBn && template.nameBn) ? template.nameBn : template.name;
    const catName = (isBn && template.categoryRef?.nameBn) ? template.categoryRef.nameBn : template.categoryRef?.name;
    const [measuredRatio, setMeasuredRatio] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const toggling = togglingId === template.id;
    const duplicating = duplicatingId === template.id;
    const editPath = `/admin/ai-templates/${template.id}/edit`;

    return (
        // A real link (not onClick+navigate) so the browser offers open-in-new-tab,
        // middle-click and cmd/ctrl+click. Nested action buttons preventDefault so
        // they don't trigger the link navigation.
        <Link
            to={editPath}
            className="group block mb-4 rounded-2xl overflow-hidden border border-[#ECE9E3] dark:border-zinc-700 bg-white dark:bg-zinc-900 cursor-pointer relative transition-all duration-150 hover:shadow-lg hover:-translate-y-0.5 hover:border-[#E2702E]/40"
        >
            <div className="relative">
                {template.thumbnail ? (
                    <div
                        className="relative w-full overflow-hidden bg-[#F0EFEC] dark:bg-zinc-800"
                        style={{ aspectRatio: IMG_ASPECT_RATIO[template.imageSize] || (measuredRatio ? `1 / ${measuredRatio}` : '4 / 5') }}
                    >
                        {!loaded && <div className="absolute inset-0 animate-pulse bg-[#E9E7E2] dark:bg-zinc-700/60" />}
                        <img
                            src={template.thumbnail}
                            alt={displayName}
                            loading="lazy"
                            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                            onLoad={(e) => {
                                const { naturalWidth, naturalHeight } = e.currentTarget;
                                if (naturalWidth) {
                                    const r = naturalHeight / naturalWidth;
                                    setMeasuredRatio(r);
                                    onMeasure?.(template.id, r);
                                }
                                setLoaded(true);
                            }}
                            onError={() => setLoaded(true)}
                        />
                    </div>
                ) : (
                    <div className="w-full aspect-[3/4] flex items-center justify-center bg-[#F1EFEA] dark:bg-zinc-800">
                        <IconSparkles size={28} className="text-[#C3BEB4] dark:text-zinc-600" />
                    </div>
                )}

                {/* Top-left: status pill (click toggles active/draft) + Free badge */}
                <div className="absolute top-2.5 left-2.5 z-10 flex items-center gap-1.5">
                    <button
                        title={template.status === 'archived' ? t('aiTemplatesAdmin.status_archived') : (template.status === 'active' ? t('aiTemplatesAdmin.setDraft') : t('aiTemplatesAdmin.setActive'))}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (template.status !== 'archived') onToggleStatus(template); }}
                        className={`inline-flex items-center px-2.5 py-[3px] rounded-full text-[10.5px] font-semibold backdrop-blur-sm ${STATUS_PILL[template.status] || STATUS_PILL.archived} ${toggling ? 'cursor-wait' : template.status !== 'archived' ? 'cursor-pointer' : 'cursor-default'}`}
                    >
                        {toggling ? '…' : t(`aiTemplatesAdmin.status_${template.status}`)}
                    </button>
                    {template.status === 'draft' && (() => {
                        const recency = recencyOf(template.createdAt);
                        return recency && (
                            <span className={`inline-flex items-center text-[10.5px] font-bold text-white px-2.5 py-[3px] rounded-full shadow-sm backdrop-blur-sm ${recency === 'today' ? 'bg-blue-500/90' : 'bg-zinc-600/85'}`}>
                                {t(recency === 'today' ? 'aiTemplatesAdmin.createdToday' : 'aiTemplatesAdmin.createdYesterday')}
                            </span>
                        );
                    })()}
                    {template.isFree && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="inline-flex items-center text-[10.5px] font-bold text-white bg-emerald-500 px-2.5 py-[3px] rounded-full shadow-sm cursor-help">
                                    {t('profile.creditFree')}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>{t('profile.freeTemplateTooltip')}</TooltipContent>
                        </Tooltip>
                    )}
                </div>

                {/* Top-right: persistent usage count (hidden on hover so actions can take its place) */}
                {template.usageCount > 0 && (
                    <div className="absolute top-2.5 right-2.5 z-10 group-hover:opacity-0 transition-opacity">
                        <span className="inline-flex items-center px-2.5 py-[3px] rounded-full text-[10.5px] font-semibold text-white bg-black/55 backdrop-blur-sm shadow-sm">
                            {formatUses(template.usageCount)} {t('worldCupJersey.uses')}
                        </span>
                    </div>
                )}

                {/* Hover action cluster (top-right): edit + duplicate + delete */}
                <div className="absolute top-2.5 right-2.5 z-20 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            {/* No onClick — the surrounding card link handles the navigation,
                                so new-tab shortcuts work on this icon too. */}
                            <span className="w-7 h-7 rounded-lg bg-white/95 dark:bg-zinc-800/95 border border-[#ECE9E3] dark:border-zinc-700 flex items-center justify-center text-[#6E6A63] dark:text-zinc-400 hover:text-[#E2702E] shadow-sm">
                                <IconEdit size={14} />
                            </span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">{t('aiTemplatesAdmin.editTemplate')}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button disabled={duplicating} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDuplicate(template); }}
                                className="w-7 h-7 rounded-lg bg-white/95 dark:bg-zinc-800/95 border border-[#ECE9E3] dark:border-zinc-700 flex items-center justify-center text-[#6E6A63] dark:text-zinc-400 hover:text-[#E2702E] shadow-sm disabled:opacity-50">
                                {duplicating ? <span className="w-3 h-3 border border-[#E2702E] border-t-transparent rounded-full animate-spin inline-block" /> : <IconCopy size={14} />}
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">{t('aiTemplatesAdmin.duplicate')}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(); }}
                                className="w-7 h-7 rounded-lg bg-white/95 dark:bg-zinc-800/95 border border-[#ECE9E3] dark:border-zinc-700 flex items-center justify-center text-red-500 hover:text-red-600 shadow-sm">
                                <IconTrash size={14} />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">{t('aiTemplatesAdmin.delete')}</TooltipContent>
                    </Tooltip>
                </div>

                {/* Hover overlay: name/category/tags pinned to bottom (whole card click = edit) */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-black/70 via-black/10 to-black/25">
                    {/* Centered share CTA — same look as the user gallery's "use this style"
                        button. Drafts are hidden from the user side (the shared link would
                        404), so no share until the template is active. */}
                    {template.status !== 'draft' && (
                        <div className="absolute inset-0 flex items-center justify-center p-3.5">
                            <button
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShare(template); }}
                                className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#E2702E] hover:bg-[#E2702E]/90 text-white text-[12.5px] font-semibold shadow-sm transition-colors"
                            >
                                <IconShare2 size={14} />
                                {t('worldCupJersey.share.button')}
                            </button>
                        </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 px-3.5 py-2.5">
                        {/* Hover-only meta: regional (non-global) badge + creator */}
                        {(template.isGlobal === false || template.createdByName) && (
                            <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                                {template.isGlobal === false && (
                                    <span title={t('aiTemplatesAdmin.nonGlobalTooltip')} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/90 text-white backdrop-blur-sm cursor-help">
                                        <IconWorldOff size={11} />
                                        {t('aiTemplatesAdmin.nonGlobalBadge')}
                                    </span>
                                )}
                                {template.createdByName && (
                                    <span title={t('aiTemplatesAdmin.createdByTooltip')} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/20 text-white backdrop-blur-sm max-w-[150px]">
                                        <IconUser size={11} className="flex-shrink-0" />
                                        <span className="truncate">{template.createdByName}</span>
                                    </span>
                                )}
                            </div>
                        )}
                        <p className="text-[13px] font-semibold text-white truncate">{displayName}</p>
                        {catName && <p className="text-[11px] text-white/70 mt-0.5 truncate">{catName}</p>}
                        {Array.isArray(template.tags) && template.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                                {template.tags.slice(0, 3).map(tag => (
                                    <button
                                        key={tag}
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTagClick?.(tag); }}
                                        className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-white/20 text-white hover:bg-[#E2702E] transition-colors backdrop-blur-sm"
                                    >
                                        #{tag}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Link>
    );
}

// ---------------------------------------------------------------------------
// Admin template list row — compact scannable variant of the card (list view)
// ---------------------------------------------------------------------------
function AdminTemplateListRow({ template, isBn, togglingId, duplicatingId, onToggleStatus, onDuplicate, onShare, onDelete, onTagClick }) {
    const { t, i18n } = useTranslation();
    const displayName = (isBn && template.nameBn) ? template.nameBn : template.name;
    const catName = (isBn && template.categoryRef?.nameBn) ? template.categoryRef.nameBn : template.categoryRef?.name;
    const toggling = togglingId === template.id;
    const duplicating = duplicatingId === template.id;
    const editPath = `/admin/ai-templates/${template.id}/edit`;
    const created = template.createdAt
        ? new Date(template.createdAt).toLocaleDateString(i18n.language === 'bn' ? 'bn-BD' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })
        : null;
    const recency = template.status === 'draft' ? recencyOf(template.createdAt) : null;
    const tags = Array.isArray(template.tags) ? template.tags : [];

    return (
        // Same real-link pattern as the card so open-in-new-tab keeps working.
        <Link
            to={editPath}
            className="group flex items-center gap-3 px-3 py-2 rounded-xl border border-[#ECE9E3] dark:border-zinc-700 bg-white dark:bg-zinc-900 transition-all hover:border-[#E2702E]/40 hover:shadow-sm"
        >
            {/* Thumbnail */}
            {template.thumbnail ? (
                <img
                    src={template.thumbnail}
                    alt={displayName}
                    loading="lazy"
                    className="w-12 h-14 flex-shrink-0 rounded-lg object-cover bg-[#F0EFEC] dark:bg-zinc-800"
                />
            ) : (
                <div className="w-12 h-14 flex-shrink-0 rounded-lg flex items-center justify-center bg-[#F1EFEA] dark:bg-zinc-800">
                    <IconSparkles size={18} className="text-[#C3BEB4] dark:text-zinc-600" />
                </div>
            )}

            {/* Name + meta (category · creator · badges) + tags */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 min-w-0">
                    <p className="text-[13.5px] font-semibold text-[#1C1B19] dark:text-zinc-100 truncate">{displayName}</p>
                    {recency && (
                        <span className={`inline-flex flex-shrink-0 items-center px-1.5 py-px rounded-full text-[10px] font-bold text-white ${recency === 'today' ? 'bg-blue-500/90' : 'bg-zinc-600/85'}`}>
                            {t(recency === 'today' ? 'aiTemplatesAdmin.createdToday' : 'aiTemplatesAdmin.createdYesterday')}
                        </span>
                    )}
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 min-w-0">
                    {catName && <span className="flex-shrink-0 text-[11.5px] text-[#9C988F] dark:text-zinc-400 truncate max-w-[160px]">{catName}</span>}
                    {template.createdByName && (
                        <span title={t('aiTemplatesAdmin.createdByTooltip')} className="inline-flex items-center gap-1 min-w-0 text-[11.5px] text-[#9C988F] dark:text-zinc-400">
                            <span className="flex-shrink-0 text-[#C3BEB4] dark:text-zinc-600">·</span>
                            <IconUser size={11} className="flex-shrink-0" />
                            <span className="truncate max-w-[140px]">{template.createdByName}</span>
                        </span>
                    )}
                    {template.isGlobal === false && (
                        <span title={t('aiTemplatesAdmin.nonGlobalTooltip')} className="inline-flex flex-shrink-0 items-center gap-1 px-1.5 py-px rounded-full text-[10px] font-semibold bg-amber-500/15 text-amber-600 dark:text-amber-400">
                            <IconWorldOff size={10} /> {t('aiTemplatesAdmin.nonGlobalBadge')}
                        </span>
                    )}
                    {template.isFree && (
                        <span className="inline-flex flex-shrink-0 items-center px-1.5 py-px rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                            {t('profile.creditFree')}
                        </span>
                    )}
                </div>
                {tags.length > 0 && (
                    <div className="mt-1 flex items-center gap-1 overflow-hidden">
                        {tags.slice(0, 5).map(tag => (
                            <button
                                key={tag}
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTagClick?.(tag); }}
                                className="flex-shrink-0 px-1.5 py-px rounded-full text-[10px] font-medium bg-[#F1EFEA] dark:bg-zinc-800 text-[#6E6A63] dark:text-zinc-300 hover:bg-[#E2702E] hover:text-white transition-colors"
                            >
                                #{tag}
                            </button>
                        ))}
                        {tags.length > 5 && (
                            <span className="flex-shrink-0 text-[10px] text-[#9C988F] dark:text-zinc-500">+{tags.length - 5}</span>
                        )}
                    </div>
                )}
            </div>

            {/* Usage count */}
            <span className="hidden sm:block flex-shrink-0 w-16 text-right text-[12px] text-[#6E6A63] dark:text-zinc-400 whitespace-nowrap">
                {formatUses(template.usageCount || 0)} {t('worldCupJersey.uses')}
            </span>

            {/* Created date */}
            {created && (
                <span className="hidden md:block flex-shrink-0 w-24 text-right text-[12px] text-[#9C988F] dark:text-zinc-400 whitespace-nowrap">
                    {created}
                </span>
            )}

            {/* Status pill (click toggles active/draft, archived is read-only) */}
            <button
                title={template.status === 'archived' ? t('aiTemplatesAdmin.status_archived') : (template.status === 'active' ? t('aiTemplatesAdmin.setDraft') : t('aiTemplatesAdmin.setActive'))}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (template.status !== 'archived') onToggleStatus(template); }}
                className={`flex-shrink-0 inline-flex items-center justify-center w-[74px] px-2 py-[3px] rounded-full text-[10.5px] font-semibold ${STATUS_PILL[template.status] || STATUS_PILL.archived} ${toggling ? 'cursor-wait' : template.status !== 'archived' ? 'cursor-pointer' : 'cursor-default'}`}
            >
                {toggling ? '…' : t(`aiTemplatesAdmin.status_${template.status}`)}
            </button>

            {/* Actions */}
            <div className="flex-shrink-0 flex items-center gap-1">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="w-7 h-7 rounded-lg border border-[#ECE9E3] dark:border-zinc-700 flex items-center justify-center text-[#6E6A63] dark:text-zinc-400 group-hover:text-[#E2702E] transition-colors">
                            <IconEdit size={14} />
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">{t('aiTemplatesAdmin.editTemplate')}</TooltipContent>
                </Tooltip>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button disabled={duplicating} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDuplicate(template); }}
                            className="w-7 h-7 rounded-lg border border-[#ECE9E3] dark:border-zinc-700 flex items-center justify-center text-[#6E6A63] dark:text-zinc-400 hover:text-[#E2702E] transition-colors disabled:opacity-50">
                            {duplicating ? <span className="w-3 h-3 border border-[#E2702E] border-t-transparent rounded-full animate-spin inline-block" /> : <IconCopy size={14} />}
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="top">{t('aiTemplatesAdmin.duplicate')}</TooltipContent>
                </Tooltip>
                {/* Drafts are hidden from the user side (the shared link would 404). */}
                {template.status !== 'draft' && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShare(template); }}
                                className="w-7 h-7 rounded-lg border border-[#ECE9E3] dark:border-zinc-700 flex items-center justify-center text-[#6E6A63] dark:text-zinc-400 hover:text-[#E2702E] transition-colors">
                                <IconShare2 size={14} />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">{t('worldCupJersey.share.button')}</TooltipContent>
                    </Tooltip>
                )}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(); }}
                            className="w-7 h-7 rounded-lg border border-[#ECE9E3] dark:border-zinc-700 flex items-center justify-center text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                            <IconTrash size={14} />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent side="top">{t('aiTemplatesAdmin.delete')}</TooltipContent>
                </Tooltip>
            </div>
        </Link>
    );
}

function formatUses(n) {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return String(n);
}

function SkeletonList() {
    return (
        <div className="flex flex-col gap-2">
            {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-[72px] rounded-xl border border-[#ECE9E3] dark:border-zinc-700 bg-[#F0EFEC] dark:bg-zinc-800 animate-pulse" />
            ))}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Skeleton masonry
// ---------------------------------------------------------------------------
function SkeletonMasonry() {
    const heights = ['h-[260px]', 'h-[200px]', 'h-[300px]', 'h-[230px]', 'h-[280px]', 'h-[210px]', 'h-[320px]', 'h-[240px]', 'h-[260px]'];
    return (
        <Masonry breakpointCols={masonryBreakpoints} className="flex w-auto -ml-4" columnClassName="pl-4 bg-clip-padding">
            {heights.map((h, i) => (
                <div key={i} className={`mb-4 rounded-2xl overflow-hidden border border-[#ECE9E3] dark:border-zinc-700 bg-[#F0EFEC] dark:bg-zinc-800 animate-pulse ${h}`} />
            ))}
        </Masonry>
    );
}
